import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { ArrowsHorizontal, ArrowsLeftRight, ChatCircle, PhoneOutgoing, VideoCamera } from "@phosphor-icons/react"
import { t } from "i18next"
import { PartnerTypeAppointmentTags } from "@/components/appointment-tags"
import { AvatarTooltip } from "@/components/avatar-tooltip"
import { UserAvatarTooltip } from "@/components/avatar-user-tooltip"
import { TextWithTooltip } from "@/components/text-with-tooltip"
import { Tag } from "@/components/ui/tag"
import { UserProvider } from "@/components/user-wrapper"
import { formatDate } from "@/lib/format-date"
import { convertLanguageToISOCode } from "@/lib/language-to-iso-code"
import { CareProviderIcon } from "@/modules/care-provider/components/care-provider-icon"
import { combineCareProviderTitle } from "@/modules/care-provider/utils/combine-care-provider-title"
import { AgendaAction, AgendaEntryStatus } from "@/modules/patients/agenda"
import { AgendaStatusCell } from "@/modules/patients/agenda/agenda-status-cell"
import { AgendaTableMenu } from "@/modules/patients/agenda/agenda-table-menu"
import { AppointmentActionMenu } from "@/modules/patients/agenda/teleconsultation/appointment-action-menu/appointment-action-menu"
import { isMedgateConnect } from "@/modules/patients/agenda/teleconsultation/booking/constants/medgate-connect"
import { AppointmentAgenda } from "@/modules/patients/libs/categorize-date-ranges"
import { FileUploadRequestFragment$key } from "@/modules/patients/services/__generated__/FileUploadRequestFragment.graphql"
import { FileUploadRequestFragment } from "@/modules/patients/services/FileUploadRequestFragment"
import { delayFormat } from "@/modules/waiting-room/helpers/delay-format"

const DateCell = ({
  original,
  agendaEntryStatus,
}: {
  original: AppointmentAgenda
  agendaEntryStatus: AgendaEntryStatus
}) => {
  const { delay, dateSort, dateTo, dateFrom, __typename } = original
  switch (__typename) {
    case "ch_AlternativeTriage":
    case "de_AlternativeTriage":
    case "ch_ExternalAssistance":
      return (
        <div data-testid="date-cell" className="truncate">
          {formatDate(dateSort, "dd.MM.yyyy, HH:mm")}
        </div>
      )
    case "Appointment": {
      return (
        <div className="flex gap-3">
          <div data-testid="date-cell">{formatDate(dateSort, "dd.MM.yyyy, HH:mm")}</div>
          {delay && agendaEntryStatus !== "PLANNED" ? (
            <Tag
              className="max-w-15"
              data-testid="delay-time-cell"
              type="warning"
              hasIcon={false}
              label={delayFormat(delay)}
            />
          ) : null}
        </div>
      )
    }
    case "ch_TimeWindow":
      return (
        <div data-testid="date-cell" className="truncate">
          {`${formatDate(dateFrom)} - ${formatDate(dateTo)}`}
        </div>
      )
  }
}

const AssignedCell = ({ original }: { original: AppointmentAgenda }) => {
  const { assignedTo, id, referralToCareProvider, __typename } = original

  switch (__typename) {
    case "Appointment":
      return (
        <UserProvider
          userName={assignedTo}
          render={(user) => (
            <div className="flex items-center" data-testid="assignedTo-cell">
              <AvatarTooltip
                firstName={user.firstName}
                lastName={user.lastName}
                professionalTitle={user.professionalTitle}
                role={user.role}
                uniqueIdentifier={`agenda-assigned-to-${id}`}
              />
              <span className="ml-2 truncate">{user.firstName + " " + user.lastName}</span>
            </div>
          )}
        />
      )
    case "ch_TimeWindow":
      return (
        referralToCareProvider && (
          <div className="flex items-center" data-testid="assignedTo-cell">
            {referralToCareProvider && (
              <>
                <CareProviderIcon
                  iconAvatarClassName="h-6 w-6"
                  width={20}
                  height={20}
                  groupType={referralToCareProvider.careProviderGroup.groupType}
                  careProvider={referralToCareProvider}
                  isHoverEnabled
                />
                <span className="ml-2 truncate">{combineCareProviderTitle(referralToCareProvider)}</span>
              </>
            )}
          </div>
        )
      )
    case "ch_AlternativeTriage":
    case "de_AlternativeTriage":
    case "ch_ExternalAssistance":
      return null
  }
}

const LanguageCell = ({ original }: { original: AppointmentAgenda }) => {
  const { language, __typename } = original
  switch (__typename) {
    case "ch_AlternativeTriage":
    case "de_AlternativeTriage":
    case "ch_ExternalAssistance":
    case "Appointment":
      return (
        <div className="truncate" data-testid="language-cell">
          {language && convertLanguageToISOCode(language)}
        </div>
      )
    case "ch_TimeWindow":
      return null
  }
}

const ChannelCell = ({ original }: { original: AppointmentAgenda }) => {
  const { channel } = original
  switch (channel) {
    case "VIDEO":
      return <VideoCamera className="text-on-surface-subdued" />
    case "CHAT":
      return <ChatCircle className="text-on-surface-subdued" />
    case "ASYNC_REQUEST":
      return <ArrowsLeftRight className="text-on-surface-subdued" />

    case "PHONE":
    case undefined:
      return null
  }
}

const AccessCell = ({ original }: { original: AppointmentAgenda }) => {
  const { access, status, phoneNumber, __typename } = original
  switch (__typename) {
    case "Appointment":
      return (
        <div className="flex gap-2">
          {status === "OPEN" && <PhoneOutgoing className="text-on-surface-subdued shrink-0" />}
          <TextWithTooltip data-testid="access-cell" className="truncate">
            {t(`scheduling.waitingRoom.access.${access}`)}
          </TextWithTooltip>
        </div>
      )
    case "ch_TimeWindow":
      return (
        <div data-testid="access-cell">
          <ArrowsHorizontal className="text-on-surface-subdued" />
        </div>
      )
    case "ch_AlternativeTriage":
    case "de_AlternativeTriage":
    case "ch_ExternalAssistance":
      return (
        <div className="flex gap-2">
          <PhoneOutgoing className="text-on-surface-subdued shrink-0" />
          <span data-testid="access-cell">{phoneNumber}</span>
        </div>
      )
  }
}

const ServiceSpecialtyCell = ({ original }: { original: AppointmentAgenda }) => {
  const { service, specialty, timeWindowId, __typename, alternativeTriage: timeWindowWithAlternativeTriage } = original
  switch (__typename) {
    case "Appointment":
      return (
        <TextWithTooltip data-testid="service-cell">
          {t(`scheduling.waitingRoom.service.${service}`)}, {t(`scheduling.waitingRoom.serviceSpecialty.${specialty}`)}
        </TextWithTooltip>
      )
    case "ch_TimeWindow":
      return (
        <div data-testid="service-cell">
          {t(
            timeWindowWithAlternativeTriage
              ? "patient.agenda.timeWindowWithAlternativeTriage"
              : "patient.agenda.timeWindow",
          )}
        </div>
      )
    case "ch_ExternalAssistance":
      return <div data-testid="service-cell">{t("patient.create.callReason.externalAssistance.title")}</div>
    case "ch_AlternativeTriage":
    case "de_AlternativeTriage":
      return (
        <div data-testid="service-cell">
          {timeWindowId
            ? t("patient.agenda.alternativeTriage.withTimeWindow")
            : t("patient.agenda.alternativeTriage.withoutTimeWindow")}
        </div>
      )
    default:
      return <div data-testid="service-cell"></div>
  }
}

const ReasonCell = ({ original }: { original: AppointmentAgenda }) => {
  const { t } = useTranslation()

  const { referralReason, reason, __typename, appointment } = original

  const fileUploadRequestData = useFragment<FileUploadRequestFragment$key>(
    FileUploadRequestFragment,
    appointment?.fileUploadRequest,
  )

  const isMedgateConnectWaitingForDocuments =
    original.service &&
    isMedgateConnect(original.service) &&
    original.appointment?.appointmentType === "FOLLOW_UP" &&
    original.appointment?.medgateConnectOrder?.status === "OPEN"

  const cellContent = () => {
    switch (__typename) {
      case "ch_TimeWindow":
        return referralReason ? t(`patient.referralReason.${referralReason}`) : ""
      case "ch_AlternativeTriage":
      case "de_AlternativeTriage":
      case "ch_ExternalAssistance":
      case "Appointment":
        return reason
    }
  }

  return (
    <div data-testid="reason-cell" className="text-on-surface-subdued flex gap-1 truncate">
      {original.appointment?.tags && <PartnerTypeAppointmentTags tags={original.appointment.tags} />}
      {(fileUploadRequestData?.status === "PENDING" || isMedgateConnectWaitingForDocuments) && (
        <Tag
          data-testid="appointment-tag-wait-for-document"
          type="warning"
          label={t("scheduling.waitingRoom.requestDocument.waitingForDocument")}
        />
      )}
      <div className="truncate">{cellContent()}</div>
    </div>
  )
}

const StatusCell = ({ original }: { original: AppointmentAgenda }) => {
  const { patient, __typename, appointment, completedBy, id, createdAt, createdBy } = original
  switch (__typename) {
    case "Appointment":
      return (
        patient &&
        appointment && (
          <div className="truncate" data-testid="status-cell">
            <AgendaStatusCell appointment={appointment} patient={patient} />
          </div>
        )
      )
    case "ch_TimeWindow":
      return
    case "ch_AlternativeTriage":
    case "de_AlternativeTriage":
    case "ch_ExternalAssistance":
      return (
        <div className="flex items-center gap-2 truncate" data-testid="status-cell">
          {createdAt && (
            <>
              <UserProvider
                userName={createdBy ?? completedBy}
                render={(user) => <UserAvatarTooltip user={user} uniqueIdentifier={id} size="xs" />}
              />
              {t("common.date.completedAt", { date: formatDate(new Date(createdAt), "HH:mm") })}
            </>
          )}
        </div>
      )
  }
}

type ActionCellEvent = {
  handleRowClick: (ch_TimeWindow: AppointmentAgenda, mode: AgendaAction) => void
}
type ActionCellProps = { original: AppointmentAgenda; isEditMode: boolean; agendaEntryStatus: AgendaEntryStatus }

const ActionCell = (props: ActionCellEvent & ActionCellProps) => {
  const { original, isEditMode, agendaEntryStatus, handleRowClick } = props
  const { __typename, appointment } = original

  switch (__typename) {
    case "Appointment":
      return (
        appointment && (
          <AppointmentActionMenu
            onHandleRowClick={handleRowClick}
            rowData={original}
            agendaEntryStatus={agendaEntryStatus}
          />
        )
      )
    case "ch_TimeWindow":
      return isEditMode && !appointment && <AgendaTableMenu onHandleRowClick={handleRowClick} rowData={original} />
    case "ch_AlternativeTriage":
    case "de_AlternativeTriage":
    case "ch_ExternalAssistance":
      return null
  }
}

export {
  AccessCell,
  ActionCell,
  AssignedCell,
  ChannelCell,
  DateCell,
  LanguageCell,
  ReasonCell,
  ServiceSpecialtyCell,
  StatusCell,
}
